import { create } from 'zustand';

/** Platforms that can have a per-product trial contract. */
export type TrialPlatform = 'google' | 'meta';

/**
 * Maps TrialPlatform keys to their productCode in the enrolled-products API.
 * Shared by useTrialSync (for reverse lookup) and requireContract (for forward lookup).
 */
export const PLATFORM_PRODUCT_CODES: Record<TrialPlatform, string> = {
  google: 'google-ads',
  meta: 'meta-ads',
};

/** Contract type values from the enrolled-products API. */
export type PlatformContractType = 'TRIAL_POC' | 'LIVE';

interface TrialState {
  /**
   * Per-platform contract type for enrolled products.
   * Key present = product enrolled; value is its contractType (or null if API returned null).
   * Not persisted — re-derived every session via useTrialSync.
   */
  enrolledContracts: Partial<Record<TrialPlatform, PlatformContractType | null>>;
  /**
   * True once useTrialSync has resolved contract data (or applied the dev
   * override). Consumers that must not render before the contract state is known
   * — e.g. the trial banner, to avoid a flash — gate on this. `enrolledContracts`
   * is `{}` both before load and for a genuinely-not-enrolled account, so it
   * can't distinguish the two on its own.
   */
  isLoaded: boolean;
  setEnrolledContracts: (
    contracts: Partial<Record<TrialPlatform, PlatformContractType | null>>,
  ) => void;
}

/**
 * Holds per-platform contract types for the active subsidiary.
 * Not persisted — re-derived from enrolled products on every session via useTrialSync.
 */
export const useTrialStore = create<TrialState>()((set) => ({
  enrolledContracts: {},
  isLoaded: false,
  setEnrolledContracts: (contracts) => {
    // Setting contracts implies the data has resolved — only useTrialSync calls
    // this, and only after the query settles (or via the dev override).
    set({ enrolledContracts: contracts, isLoaded: true });
  },
}));
